import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// Platform-agnostic checkout header branding for the checkout-app shell. The
// RPC contract and app only ever see this shape, never a platform's API shape.

export enum CheckoutChromeLogoPosition {
  START = "start",
  CENTER = "center",
  END = "end",
}

export const checkoutChromeLogoPosition: z.ZodType<CheckoutChromeLogoPosition> =
  createEnum(CheckoutChromeLogoPosition);

// Resolved primary-button styling in concrete px/hex — the service maps the
// platform's enum tokens down to these.
const checkoutChromePrimaryButton_ = z.object({
  backgroundColor: z.string(),
  textColor: z.string(),
  hoverBackgroundColor: z.string().nullable(),
  hoverTextColor: z.string().nullable(),
  cornerRadiusPx: z.number().nonnegative(),
  fontSizePx: z.number().positive(),
  paddingBlockPx: z.number().nonnegative(),
  paddingInlinePx: z.number().nonnegative(),
});
export const checkoutChromePrimaryButton: z.ZodType<CheckoutChromePrimaryButton> =
  checkoutChromePrimaryButton_;
export interface CheckoutChromePrimaryButton extends z.infer<
  typeof checkoutChromePrimaryButton_
> {}

// Resolved order-summary styling; null = shell default. bar* style the mobile
// disclosure bar (tints Shopify derives from the main scheme background).
const checkoutChromeOrderSummary_ = z.object({
  backgroundColor: z.string().nullable(),
  backgroundImageUrl: z.string().url().nullable(),
  barBackgroundColor: z.string().nullable(),
  barBorderColor: z.string().nullable(),
});
export const checkoutChromeOrderSummary: z.ZodType<CheckoutChromeOrderSummary> =
  checkoutChromeOrderSummary_;
export interface CheckoutChromeOrderSummary extends z.infer<
  typeof checkoutChromeOrderSummary_
> {}

// Resolved header styling; null = shell default. borderColor paints the bottom
// border, iconColor the cart icon; Shopify's header "banner" image is unmapped.
const checkoutChromeHeader_ = z.object({
  backgroundColor: z.string().nullable(),
  borderColor: z.string().nullable(),
  iconColor: z.string().nullable(),
});
export const checkoutChromeHeader: z.ZodType<CheckoutChromeHeader> =
  checkoutChromeHeader_;
export interface CheckoutChromeHeader extends z.infer<
  typeof checkoutChromeHeader_
> {}

// Resolved main (left column) styling; background image unmapped. borderColor
// paints the desktop column divider (Shopify draws it on main, not the rail).
const checkoutChromeMain_ = z.object({
  backgroundColor: z.string().nullable(),
  borderColor: z.string().nullable(),
});
export const checkoutChromeMain: z.ZodType<CheckoutChromeMain> =
  checkoutChromeMain_;
export interface CheckoutChromeMain extends z.infer<
  typeof checkoutChromeMain_
> {}

const checkoutChrome_ = z.object({
  faviconUrl: z.string().url().nullable(),
  logoPosition: checkoutChromeLogoPosition,
  logoUrl: z.string().url().nullable(),
  logoMaxWidthPx: z.number().int().positive().nullable(),
  header: checkoutChromeHeader,
  main: checkoutChromeMain,
  orderSummary: checkoutChromeOrderSummary,
  primaryButton: checkoutChromePrimaryButton,
  showCartLink: z.boolean(),
});
export const checkoutChrome: z.ZodType<CheckoutChrome> = checkoutChrome_;
export interface CheckoutChrome extends z.infer<typeof checkoutChrome_> {}

// What the shell renders with no merchant branding; mirrors Shopify's
// uncustomized checkout defaults (button values measured from stock checkouts).
export const DEFAULT_CHECKOUT_CHROME: CheckoutChrome = {
  faviconUrl: null,
  // Shopify left-aligns the logo when the merchant never set an alignment.
  logoPosition: CheckoutChromeLogoPosition.START,
  logoUrl: null,
  logoMaxWidthPx: null,
  header: { backgroundColor: null, borderColor: null, iconColor: null },
  main: { backgroundColor: null, borderColor: null },
  orderSummary: {
    backgroundColor: null,
    backgroundImageUrl: null,
    barBackgroundColor: null,
    barBorderColor: null,
  },
  primaryButton: {
    // Stock button blue, measured 2026-08-27 on an unbranded checkout.
    backgroundColor: "#005bd1",
    textColor: "#ffffff",
    hoverBackgroundColor: null,
    hoverTextColor: null,
    cornerRadiusPx: 12,
    // Measured 14px on stock mobile and desktop checkouts (2026-08-27).
    fontSizePx: 14,
    paddingBlockPx: 14,
    paddingInlinePx: 14,
  },
  showCartLink: true,
};
